import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { buildSeo } from "@/lib/seo";
import { getClaimGuide, type ClaimGuide } from "@/data/claim-guides";
import { RouteNotFound } from "@/components/RouteNotFound";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

function jsonLd(guide: ClaimGuide) {
  const url = `https://usalotteryclub.com/states/${guide.stateSlug}/claim-prize`;
  return [
    {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: `How to claim a ${guide.lotteryName} prize`,
      description: `Step-by-step guide to claiming ${guide.stateName} lottery winnings, including claim tiers, deadlines, documents and taxes.`,
      url,
      step: guide.tiers.map((t, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: `Prize of ${t.amount}`,
        text: t.where,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: guide.faqs.map((f) => ({
        "@type": "Question",
        name: f.question,
        acceptedAnswer: { "@type": "Answer", text: f.answer },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://usalotteryclub.com/" },
        { "@type": "ListItem", position: 2, name: "States", item: "https://usalotteryclub.com/states" },
        {
          "@type": "ListItem",
          position: 3,
          name: guide.stateName,
          item: `https://usalotteryclub.com/states/${guide.stateSlug}`,
        },
        { "@type": "ListItem", position: 4, name: "Claim a prize", item: url },
      ],
    },
  ];
}

export const Route = createFileRoute("/states/$stateSlug/claim-prize")({
  loader: ({ params }) => {
    const guide = getClaimGuide(params.stateSlug);
    if (!guide) throw notFound();
    return { guide };
  },
  head: ({ params }) => {
    const guide = getClaimGuide(params.stateSlug);
    if (!guide) return {};
    return buildSeo({
      title: `How to Claim a ${guide.stateName} Lottery Prize: Rules, Deadlines & Taxes`,
      description: `Where to claim ${guide.stateName} lottery winnings by prize amount, the ${guide.stateName} claim deadline, required documents, tax withholding and anonymity rules — sourced from the ${guide.lotteryName}.`,
      path: `/states/${guide.stateSlug}/claim-prize`,
      type: "article",
      jsonLd: jsonLd(guide),
    });
  },
  component: ClaimPrizePage,
  notFoundComponent: () => (
    <RouteNotFound
      title="No claim guide yet"
      description="We haven't published a prize-claim guide for this state yet. Check the state hub for official lottery links."
    />
  ),
});

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section aria-labelledby={id} className="mt-10">
      <h2 id={id} className="font-display text-xl font-bold mb-3">{title}</h2>
      {children}
    </section>
  );
}

function ClaimPrizePage() {
  const { guide } = Route.useLoaderData();

  return (
    <Layout>
      <div className="container mx-auto max-w-4xl px-4 py-8">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/states">States</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/states/$stateSlug" params={{ stateSlug: guide.stateSlug }}>{guide.stateName}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>Claim a prize</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <h1 className="mt-6 font-display text-3xl md:text-4xl font-bold tracking-tight">
          How to claim a {guide.stateName} lottery prize
        </h1>
        <p className="mt-4 text-muted-foreground" data-speakable>{guide.intro}</p>

        <Section id="where-to-claim" title={`Where to claim, by prize amount`}>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[38%]">Prize amount</TableHead>
                    <TableHead>Where to claim it</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {guide.tiers.map((t) => (
                    <TableRow key={t.amount}>
                      <TableCell className="font-medium align-top">{t.amount}</TableCell>
                      <TableCell className="text-muted-foreground">{t.where}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Section>

        <Section id="deadline" title="Claim deadline">
          <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            {guide.deadline.map((d) => <li key={d}>{d}</li>)}
          </ul>
        </Section>

        <Section id="documents" title="What you need to bring or send">
          <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            {guide.documents.map((d) => <li key={d}>{d}</li>)}
          </ul>
          <p className="mt-3 text-sm">
            <span className="font-semibold">Mail-in address:</span>{" "}
            <span className="text-muted-foreground">{guide.mailAddress}</span>
          </p>
        </Section>

        <Section id="claim-centers" title={`${guide.stateName} claim center locations`}>
          <p className="text-sm text-muted-foreground">{guide.claimCenterHours}</p>
          <ul className="mt-3 flex flex-wrap gap-2">
            {guide.claimCenterCities.map((c) => (
              <li key={c} className="rounded-full border border-border bg-muted/40 px-3 py-1 text-sm">{c}</li>
            ))}
          </ul>
        </Section>

        <Section id="taxes" title="Tax withholding on winnings">
          <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            {guide.taxes.map((t) => <li key={t}>{t}</li>)}
          </ul>
          <p className="mt-3 text-sm">
            <Link
              to="/tools/tax-calculator/$stateSlug"
              params={{ stateSlug: guide.stateSlug }}
              className="text-primary hover:underline"
            >
              Estimate your {guide.stateName} take-home with the tax calculator →
            </Link>
          </p>
        </Section>

        <Section id="lump-sum" title="Lump sum or annuity?">
          <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            {guide.payoutChoice.map((p) => <li key={p}>{p}</li>)}
          </ul>
          <p className="mt-3 text-sm">
            <Link to="/tools/payout-calculator" className="text-primary hover:underline">
              Compare lump sum vs annuity payouts →
            </Link>
          </p>
        </Section>

        <Section id="anonymity" title={`Can you stay anonymous in ${guide.stateName}?`}>
          <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            {guide.anonymity.map((a) => <li key={a}>{a}</li>)}
          </ul>
        </Section>

        <Section id="faqs" title={`${guide.stateName} prize-claim FAQs`}>
          <div className="grid gap-3 md:grid-cols-2">
            {guide.faqs.map((f) => (
              <div key={f.question} className="rounded-lg border border-border bg-muted/30 px-4 py-3">
                <h3 className="text-sm font-semibold">{f.question}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{f.answer}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section id="sources" title="Sources">
          <p className="text-sm text-muted-foreground">
            Every figure on this page was checked against official {guide.stateName} sources on{" "}
            {new Date(`${guide.lastReviewed}T00:00:00Z`).toLocaleDateString("en-US", {
              year: "numeric", month: "long", day: "numeric", timeZone: "UTC",
            })}
            . Rules change — confirm with the {guide.lotteryName} before you claim.
          </p>
          <ul className="mt-3 list-disc space-y-1 pl-5 text-sm">
            {guide.sources.map((s) => (
              <li key={s.url}>
                <a href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="text-primary hover:underline">
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </Section>

        <p className="mt-8 text-sm">
          <Link to="/states/$stateSlug" params={{ stateSlug: guide.stateSlug }} className="text-primary hover:underline">
            ← {guide.stateName} lottery results and games
          </Link>
        </p>
      </div>
    </Layout>
  );
}
